// @flow
import { get } from 'lodash';
import domainsService from '@clubmed/domains-service';
import { constants } from '@clubmed/utils';

import { getCxoAttendeesData, getResort } from 'Views/booking/BookingEngine.query';
import validationSchemaAddressFragment from 'Store/validation-schema/validationSchema.address.fragment';
import type { Kids as KidsType } from 'Store/trip-information/kids/kids.reducer';
import type {
  State as ArrayAdultsType,
  Adults as AdultsType,
} from 'Store/trip-information/adults/adults.reducer';
import { profileFragment } from 'Components/widgets/contact-info/ContactInfo.fragment';
import BookingAttendeesProfile from 'Components/widgets/contact-info/BookingAttendeesProfile.fragment';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import graphQLClient from 'Utils/graphql-client/graphqlClient';
import {
  PROPOSAL_ACCOMMODATION_ADD,
  PROPOSAL_ADD_TRANSPORT,
  PROPOSAL_GET_SCHEDULE_PAYMENT,
  PROPOSAL_GET_SEVERAL,
  PROPOSAL_USE_PROMO_CODE,
  PROPOSAL_SWITCH_OFFER,
  PROPOSAL_ADDITIONAL_SERVICES_ADD,
  PROPOSAL_ADDITIONAL_SERVICES_UPDATE,
  PROPOSAL_ADDITIONAL_SERVICES_DELETE,
} from 'Utils/request/requestDictionary';
import { timestampToApiDate } from 'Utils/date/apiDate';
import { graphqlify } from 'Utils/graphql';

const { JOYVIEW_ROOM_ONLY, JOYVIEW_ALL_INCLUSIVE, JOYVIEW_FOOD_AND_BEVERAGES } = constants.PACKAGES;

type ProposalsQueryParam = {
  arrivalDate: string,
  adults: ArrayAdultsType,
  kids: $ReadOnlyArray<{
    +birthdate: timestamp,
    +childcareIncluded: boolean,
    +neolid?: string,
  }>,
  departureCity?: string,
  duration: number,
  isJoyView?: boolean,
  numberAttendees: number,
  productId: string,
  transferCode?: boolean,
  adultsAttendeesIds?: Array<string>,
  kidsAttendeesIds?: Array<string>,
  fetchActivityCategories?: boolean,
  salesNetworkId?: string,
};

function getPriceDetailFragment(locale: string): string {
  const precision = domainsService.getBookingPrecision(locale);
  return `
    fragment priceDetails on PriceDetailType {
      value
      formattedValue(precision : ${precision})
    }
  `;
}

const transportDetailsFragment = `
  fragment transportsDetails on TransportDetailType {
    durationInMin
    travelSections {
      departure {
        id
        cityId
        label
        airportLabel
        terminalCode
        date
        time
      }
      arrival {
        id
        label
        airportLabel
        terminalCode
        date
        time
      }
      technicalStops
      connectionDurationInMin
      stopDurationInMin
      transport {
        type
        class
        travelNumber
        baggage {
          quantity
          weight
        }
        companyOperator {
          id
          label
          logo
        }
      }
    }
    cancellationPolicyType
    isClubmedTransport
  }
`;

export const childcaresQuery = `
childcares {
  pricingInfo {
    id
    isIncluded
    price: startingPricePerAdult {
      ...priceDetails
      formattedValueRounded : formattedValue(precision : 0)
    }
    isPartiallyRented
    type
    rentedAmount {
      value
    }
    schedules {
      startDate
      endDate
      remainingStock
      attendees {
        id
        isRented
      }
    }
  }
  productInfo {
    id
    label
    images
    description
    durationDescription
    isIncluded
    ageInMonths {
      min
      max
    }
  }
}`;
export const transfersQuery = `
transfers {
  pricingInfo {
    id
    isIncluded
    price: startingPricePerAdult {
      ...priceDetails
      formattedValueRounded : formattedValue(precision : 0)
    }
    totalPrice {
      value
      formattedValue
      currency
    }
    isPartiallyRented
    isFullyRented
    type
    rentedAmount {
      value
    }
    schedules {
      startDate
      endDate
      remainingStock
      attendees {
        id
        isRented
      }
    }
  }
  productInfo {
    id
    description
    durationInMinutes
    vehicle {
      id
      label
    }
    label
  }
}
`;
const additionalServicesQuery = `
  additionalServices(types: [EXCURSION, RENTAL, CHILDCARE, LAND_SPORT, WATER_SPORT, WINTER_SPORT, TRANSFER]) {
    activities {
      pricingInfo {
        id
        type
        timeSlot
        rentedAmount {
          formattedValue
          value
          currency
        }
        startingPricePerAdult {
          formattedValue
          value
        }
        isPartiallyRented
        schedules {
          startDate
          endDate
          attendees {
            id
            isRented
            price {
              formattedValue
              value
            }
          }
        }
      }
      productInfo {
        id
        label
        isIncluded
        parentActivityCategoriesId
      }
    }
    rentals {
      pricingInfo {
        id
        type
        rentedAmount {
          value
          formattedValue
          currency
        }
        startingPricePerAdult {
          value
          formattedValue
        }
        isPartiallyRented
        schedules {
          startDate
          endDate
          attendees {
            id
            isRented
            price {
              formattedValue
              value
            }
          }
        }
      }
      productInfo {
        id
        label
        ageInMonths {
          min
          max
        }
        useCase {
          id
          label
        }
        typeId: type
        typeLabel
        description: longDescription
        sizes {
          min
          max
          unit
        }
        practiceLevels {
          id
          label
        }
      }
    }
    ${childcaresQuery}
    ${transfersQuery}
  }
`;

function proposalPriceQuery(): string {
  return `
    price {
      total {...priceDetails}
      totalWithoutDiscount {...priceDetails}
      aggregateDiscount {
        offerColor
        offerName
        amount {...priceDetails}
      }
      aggregateDiscountPromoCode {
        offerColor
        offerName
        amount {...priceDetails}
      }
      promoCode {
        offerColor
        offerName
        amount {
          value
          formattedValue
        }
      }
      totalWithoutChildcare {...priceDetails}
      totalWithoutChildcareAndDiscount {...priceDetails}
      initialPrice {...priceDetails}
      discounts {
        amount {...priceDetails}
        offerId
        offerName
        offerColor
      }
      transfer {...priceDetails}
      fees {
        type
        amount {...priceDetails}
      }
      childcare {...priceDetails}
      rental {...priceDetails}
      excursions {...priceDetails}
      sports {...priceDetails}
      currency
      hasFlexRates
      selectedRate
      yieldPricingVariation
      gap
    }
    alternativePrice {
      total {...priceDetails}
      totalWithoutDiscount {...priceDetails}
      aggregateDiscount {
        offerColor
        offerName
        amount {...priceDetails}
      }
      aggregateDiscountPromoCode {
        offerColor
        offerName
        amount {...priceDetails}
      }
      promoCode {
        offerColor
        offerName
        amount {
          value
          formattedValue
        }
      }
      totalWithoutChildcare {...priceDetails}
      totalWithoutChildcareAndDiscount {...priceDetails}
      initialPrice {...priceDetails}
      discounts {
        amount {...priceDetails}
        offerId
        offerName
        offerColor
      }
      transfer {...priceDetails}
      fees {
        type
        amount {...priceDetails}
      }
      childcare {...priceDetails}
      rental {...priceDetails}
      currency
    }
    `;
}

function insurancesQuery() {
  return `
    listInsurances {
      id
      label
      subscribed
      price {
        ...priceDetails
      }
      insuranceDetails{
        id
        label
        images
        description
        shortDescription
        videos
        attachedDocuments {
          label
          url
        }
        company {
          label
          logo
        }
        coverageDescriptions
        subscriptionConditions {
          age_min
          ageMax
          byAttendees
        }
      }
    }`;
}

function proposalTransfersQuery() {
  return `
    availableTransfers {
      ...transferDetails
    }
    selectedTransfers {
      ...transferDetails
    }
  `;
}
function proposalFields(params: { isJoyView?: boolean, isCmta: boolean }) {
  return `
    id
    packageId
    productId
    originalLocale
    ${additionalServicesQuery}
    ${
      params.isJoyView
        ? `
    alternativePackages {
      proposalId
      packageId
      upsellPrice
    }
    packages(packageIds : ["${JOYVIEW_ROOM_ONLY}", "${JOYVIEW_ALL_INCLUSIVE}", "${JOYVIEW_FOOD_AND_BEVERAGES}"]) {
      id
      image
      additionalInfo
      description
      label
      prices {
        perAdultPerNight {
          ...priceDetails
          currency
        }
        perKidPerNight {
          ...priceDetails
          currency
        }
      }
    }`
        : ''
    }
    label
    accommodationsCount
    accommodations {
      id
      label
      description
      quantity
      connectingRoom
      familyRoom
      surface
      view {
        id
        label
      }
      strengths {
        id
        label
      }
      equipments {
        id
        label
      }
      services {
        id
        label
      }
      outsideFacilities {
        id
        label
      }
      bathroomEquipments {
        id
        label
      }
      equipments {
        id
        label
      }
      categories {
        id
      }
      category
      categoryId
      comfortType
      comfortId
      maxCapacity
      media {
        photos {
          url
        }
      }
      roomType
      attendeesDistribution {
        attendees {
          id
          type
          birthdate,
          customerId
        }
      }
    }
    duration
    media {
      photos {
        url
      }
    }
    ${proposalPriceQuery()}
    arrivalDate
    departureDate
    adultsCount
    childrenCount
    attendees {
      list {
        id
        birthdate
        customerId
      }
      adults {
        id
      }
      children(birthdateSort: asc) {
        id
        birthdate
        customerId
      }
    }
    remainingStock
    optionAvailable
    isCounterProposal
    transports {
      inbound {
        ...transportsDetails
      }
      outbound {
        ...transportsDetails
      }
    }
     ${insurancesQuery()}
    allInclusiveServices {
      icon
      label
      description
    }
    ${
      params.isCmta
        ? `sellerCommission {
            formattedValue(precision: 2)
          }`
        : ''
    }
     `;
}

const paymentScheduleRequest = `paymentSchedule {
  total {...priceDetails}
  depositAmount {...priceDetails}
  repaymentSchedules{
    amount {...priceDetails}
    deadline
  }
}`;

const paymentProvidersRequest = () => `paymentProviders {
  description
  id
  label
  logo
  paymentMethods {
    category
    id
    image
    label
    timePaymentConditions {
      chargePercentage
      id
      paymentCount
    }
  }
  requiredDelayBeforeDeparture
  token {
    format
    value
  }
}`;

function getPaymentScheduleRequest(appContext: AppContext): string {
  return isFeatureFlipped(appContext)('booking.deposit') ? paymentScheduleRequest : '';
}

function fetchPayment(proposalId: string, appContext: AppContext): Promise<*> {
  return graphQLClient.runNoCacheQuery(
    `
    {
      proposal(id: "${proposalId}") {
        ${getPaymentScheduleRequest(appContext)}
      }
      ${paymentProvidersRequest()}
    }
    ${getPriceDetailFragment(get(appContext, 'locale'))}
  `,
    appContext,
    { requestName: PROPOSAL_GET_SCHEDULE_PAYMENT },
  );
}
type Attendee = KidsType | AdultsType;

function getKnownAttendeesInformation(attendees: Array<Attendee>, neolaneId: ?string): string {
  const attendeeInfo = attendees.map((attendee, index) => {
    const { neolid = '', birthdate } = attendee;
    // neolid : Send by CXO in query params
    // neolaneId : In appContext, identify user logged
    // When not seller, set first adult with neolaneId if user is authenticated
    return {
      customer_id: index === 0 && neolaneId ? neolaneId : neolid,
      ...(birthdate ? { birthdate: timestampToApiDate(birthdate) } : null),
    };
  });
  return graphqlify(attendeeInfo);
}

function proposalsQuery(params: ProposalsQueryParam, appContext: AppContext): Promise<*> {
  const { kids, adults } = params;
  const attendees = adults && adults.length ? adults.concat(kids) : kids;
  // When quick logged neolaneId but should not call profile api
  // TODO: handle quicklogged status and not call profile API every fucking time because 401 is silent
  const neolaneId = appContext.isUserLogged ? appContext.neolaneId : null;
  // $FlowFixMe
  const knownAttendeesInformation = getKnownAttendeesInformation(attendees, neolaneId);

  const proposalArgs = [
    `productId: "${params.productId}"`,
    `arrivalDate: "${params.arrivalDate}"`,
    `duration: ${params.duration}`,
    `numberAttendees: ${params.numberAttendees}`,
    `knownAttendeesInformation: ${knownAttendeesInformation}`,
    ...(params.departureCity ? [`departureCity : "${params.departureCity}"`] : []),
    ...(params.isJoyView ? ['isJoyView: true'] : []),
    ...(params.salesNetworkId ? [`salesNetworkId: "${params.salesNetworkId}"`] : []),
    `displayCounterDurations: ${
      isFeatureFlipped(appContext)('booking.displayDurationsCounterProposals') ? 'true' : 'false'
    }`,
  ];

  return graphQLClient.runNoCacheQuery(
    `
    {
      proposals(${proposalArgs.join(', ')}) {
        ... on ErrorsProposalType {
          errors {
            errorCode
          }
        }
        ... on ProposalsType {
          proposals {
            ${proposalFields({
              ...params,
              isCmta: appContext.isCmta,
            })}
          }
          dateSuggestion {
            dateOffset
            price {
              total
            }
            resortArrivalDate
            resortDepartureDate
          }
          priceCurrency
          suggestions {
            resort {
              id
              name
              type
              badgeIcon
              href
              images
              destination {
                country
              }
              comfort
            }
            remainingStock
            initialStock
          }
          durationSuggestions {
            ids
            data {
              price {
                total {
                  formattedValue
                }
              }
              duration
              resortArrivalDate: arrivalDate
              resortDepartureDate: departureDate
            }
          }
          ${getResort(params.fetchActivityCategories)}
        }
      }
      ${appContext.isUserLogged ? 'profile { ...BookingProfile }' : ''}
      validationSchema(resource : "profile", fields: ["address"]) {
        ...ValidationSchemaAddress
      }
      ${getCxoAttendeesData(params)}
    }

    ${validationSchemaAddressFragment}
    ${appContext.isUserLogged ? profileFragment : ''}
    ${transportDetailsFragment}
    ${getPriceDetailFragment(get(appContext, 'locale'))}
    ${
      (params.adultsAttendeesIds && params.adultsAttendeesIds[0]) ||
      (params.kidsAttendeesIds && params.kidsAttendeesIds[0])
        ? BookingAttendeesProfile
        : ''
    }
  `,
    appContext,
    { requestName: PROPOSAL_GET_SEVERAL },
  );
}

function addTransportMutation(
  transportId: *,
  proposalsId: Array<string>,
  isJoyView: boolean,
  appContext: AppContext,
): Promise<*> {
  return graphQLClient.runNoCacheQuery(
    `
    mutation {
      addTransport (proposalsId: ["${proposalsId.join('","')}"], transportId: "${transportId}") {
        ${proposalFields({
          isJoyView,
          isCmta: appContext.isCmta,
        })}
      }
    }
    ${transportDetailsFragment}
    ${getPriceDetailFragment(get(appContext, 'locale'))}
    `,
    appContext,
    { requestName: PROPOSAL_ADD_TRANSPORT },
  );
}

function addAccommodationMutation(
  arrangement: AccommodationArrangement,
  proposalsId: Array<string>,
  isJoyView: boolean,
  appContext: AppContext,
): Promise<*> {
  return graphQLClient.runNoCacheQuery(
    `
    mutation {
      addAccommodation (proposalsId: ["${proposalsId.join('","')}"], arrangement: ${graphqlify(
      arrangement,
    )}) {
        ${proposalFields({
          isJoyView,
          isCmta: appContext.isCmta,
        })}
      }
    }
    ${transportDetailsFragment}
    ${getPriceDetailFragment(get(appContext, 'locale'))}
    `,
    appContext,
    { requestName: PROPOSAL_ACCOMMODATION_ADD },
  );
}

export type ServicesMutationType = $ReadOnly<{|
  id: string,
  schedules: $ReadOnlyArray<{|
    startDate: string,
    endDate: string,
    attendees: $ReadOnlyArray<{|
      id: string,
    |}>,
  |}>,
|}>;

const putServicesGraphQLQuery = (
  proposalsId: $ReadOnlyArray<string>,
  services: $ReadOnlyArray<ServicesMutationType> | ServicesMutationType,
  appContext: AppContext,
) =>
  graphQLClient.runNoCacheQuery(
    `
    mutation {
      putServices(proposalIds: ${graphqlify(proposalsId)}, services: ${graphqlify(services)}) {
        id
        ${additionalServicesQuery}
        ${proposalPriceQuery()}
      }
    }
    ${getPriceDetailFragment(appContext.locale)}
    `,
    appContext,
    { requestName: PROPOSAL_ADDITIONAL_SERVICES_UPDATE },
  );

/**
 * We don't want to have this request in parallel, it's a put and it can be really long, so we create a queue to do
 * these requests synchronously
 */
let serviceRequestRunning = null;
const putServicesMutation = async (
  proposalIds: $ReadOnlyArray<string>,
  services: $ReadOnlyArray<ServicesMutationType> | ServicesMutationType,
  appContext: AppContext,
) => {
  // if there is no queue we do the request otherwise we queue the request
  if (!serviceRequestRunning) {
    serviceRequestRunning = putServicesGraphQLQuery(proposalIds, services, appContext);

    let result;
    try {
      result = await serviceRequestRunning;
    } catch (e) {
      throw e;
    } finally {
      serviceRequestRunning = null;
    }

    return result;
  }

  // we wait for the last request to finish before run the new request
  await serviceRequestRunning;

  return putServicesMutation(proposalIds, services, appContext);
};

export type OverloadForSpecificProposalIdsType = $ReadOnly<{|
  proposalIds: $ReadOnlyArray<string>,
  serviceIds: $ReadOnlyArray<string>,
|}>;

const deleteServiceGraphQLQuery = ({
  proposalIds,
  serviceIds,
  attendeeIds,
  overloadForSpecificProposalIds,
  appContext,
}: {
  proposalIds: $ReadOnlyArray<string>,
  serviceIds: $ReadOnlyArray<string>,
  attendeeIds: $ReadOnlyArray<string> | string,
  overloadForSpecificProposalIds?: OverloadForSpecificProposalIdsType,
  appContext: AppContext,
}) =>
  graphQLClient.runNoCacheQuery(
    `
    mutation {
      deleteService(proposalIds: ${graphqlify(proposalIds)} serviceIds: ${graphqlify(
      serviceIds,
    )} attendeeIds: ${graphqlify(attendeeIds)} ${
      overloadForSpecificProposalIds
        ? `overloadForSpecificProposalIds: ${graphqlify(overloadForSpecificProposalIds)}`
        : ''
    }) {
        id
        ${additionalServicesQuery}
        ${proposalPriceQuery()}
      }
    }
    ${getPriceDetailFragment(appContext.locale)}
    `,
    appContext,
    { requestName: PROPOSAL_ADDITIONAL_SERVICES_DELETE },
  );

const deleteServicesMutation = async ({
  proposalIds,
  serviceIds,
  attendeeIds,
  overloadForSpecificProposalIds,
  appContext,
}: {
  proposalIds: $ReadOnlyArray<string>,
  serviceIds: $ReadOnlyArray<string>,
  attendeeIds: $ReadOnlyArray<string> | string,
  overloadForSpecificProposalIds?: OverloadForSpecificProposalIdsType,
  appContext: AppContext,
}) => {
  // if there is no queue we do the request otherwise we queue the request
  if (!serviceRequestRunning) {
    serviceRequestRunning = deleteServiceGraphQLQuery({
      proposalIds,
      serviceIds,
      attendeeIds,
      overloadForSpecificProposalIds,
      appContext,
    });

    let result;
    try {
      result = await serviceRequestRunning;
    } catch (e) {
      throw e;
    } finally {
      serviceRequestRunning = null;
    }

    return result;
  }

  // we wait for the last request to finish before run the new request
  await serviceRequestRunning;

  return deleteServicesMutation({
    proposalIds,
    serviceIds,
    attendeeIds,
    overloadForSpecificProposalIds,
    appContext,
  });
};

const addServiceGraphQLQuery = ({
  proposalIds,
  serviceId,
  attendeeIds,
  schedules,
  overloadForSpecificProposalIds,
  appContext,
}: {
  proposalIds: $ReadOnlyArray<string>,
  serviceId: string,
  attendeeIds: $ReadOnlyArray<string> | string,
  schedules: $ReadOnlyArray<{ startDate: string, endDate: string }>,
  overloadForSpecificProposalIds?: $ReadOnly<{|
    proposalIds: $ReadOnlyArray<string>,
    serviceId: string,
  |}>,
  appContext: AppContext,
}) =>
  graphQLClient.runNoCacheQuery(
    `
    mutation {
      addService(proposalIds: ${graphqlify(
        proposalIds,
      )}, serviceId: "${serviceId}", attendeeIds: ${graphqlify(
      attendeeIds,
    )}, schedules: ${graphqlify(schedules)}  ${
      overloadForSpecificProposalIds
        ? `overloadForSpecificProposalIds: ${graphqlify(overloadForSpecificProposalIds)}`
        : ''
    }) {
        id
        ${additionalServicesQuery}
        ${proposalPriceQuery()}
      }
    }
    ${getPriceDetailFragment(appContext.locale)}
    `,
    appContext,
    {
      requestName: PROPOSAL_ADDITIONAL_SERVICES_ADD,
    },
  );

const addServiceMutation = async ({
  proposalIds,
  serviceId,
  attendeeIds,
  schedules,
  overloadForSpecificProposalIds,
  appContext,
}: {
  proposalIds: $ReadOnlyArray<string>,
  serviceId: string,
  attendeeIds: $ReadOnlyArray<string> | string,
  schedules: $ReadOnlyArray<{ startDate: string, endDate: string }>,
  overloadForSpecificProposalIds?: OverloadForSpecificProposalIdsType,
  appContext: AppContext,
}) => {
  // if there is no queue we do the request otherwise we queue the request
  if (!serviceRequestRunning) {
    serviceRequestRunning = addServiceGraphQLQuery({
      proposalIds,
      serviceId,
      attendeeIds,
      schedules,
      ...(overloadForSpecificProposalIds && {
        overloadForSpecificProposalIds: {
          proposalIds: overloadForSpecificProposalIds.proposalIds,
          serviceId: overloadForSpecificProposalIds.serviceIds[0],
        },
      }),
      appContext,
    });

    let result;
    try {
      result = await serviceRequestRunning;
    } catch (e) {
      throw e;
    } finally {
      serviceRequestRunning = null;
    }

    return result;
  }

  // we wait for the last request to finish before run the new request
  await serviceRequestRunning;

  return addServiceMutation({
    proposalIds,
    serviceId,
    attendeeIds,
    schedules,
    overloadForSpecificProposalIds,
    appContext,
  });
};

type PromoCodeParams = {
  proposalId: string,
  promoCode: string,
  partnerId?: string,
  accessCode?: string,
  customerId: number,
};

function promoCodeProposalMutation(params: PromoCodeParams, appContext: AppContext) {
  return graphQLClient.runNoCacheQuery(
    `
    mutation {
      promoCodeProposal(id: "${params.proposalId}", promoCode: "${params.promoCode}" ${
      params.partnerId && params.accessCode
        ? `, partnerId: "${params.partnerId}", accessCode: "${params.accessCode}"`
        : ''
    }, customerId: ${params.customerId}) {
        id
        price {
          aggregateDiscount {
            offerColor
            offerName
            amount {...priceDetails}
          }
          aggregateDiscountPromoCode {
            offerColor
            offerName
            amount {...priceDetails}
         }
          promoCode {
            offerColor
            offerName
            amount {
              value
              formattedValue
            }
          }
          total {...priceDetails}
          discounts {
            amount {...priceDetails}
            offerName
            offerColor
          }
        }
        paymentSchedule {
          total {...priceDetails}
          depositAmount {...priceDetails}
          repaymentSchedules {
            amount {...priceDetails}
            deadline
          }
        }
      }
    }
    ${getPriceDetailFragment(get(appContext, 'locale'))}
    `,
    appContext,
    { requestName: PROPOSAL_USE_PROMO_CODE },
  );
}

function switchProposalsOffers(
  proposalIds: Array<string>,
  offerIds: Array<string>,
  appContext: AppContext,
) {
  return graphQLClient.runNoCacheQuery(
    `mutation {
      proposals: switchProposalsOffers(proposalIds: ["${proposalIds.join(
        '","',
      )}"], offerIds: ["${offerIds.join('","')}"]) {
        id
        optionAvailable
        ${proposalPriceQuery()}
      }
    }
    ${getPriceDetailFragment(get(appContext, 'locale'))}
    `,
    appContext,
    { requestName: PROPOSAL_SWITCH_OFFER },
  );
}

export {
  additionalServicesQuery,
  addAccommodationMutation,
  addServiceMutation,
  addTransportMutation,
  deleteServicesMutation,
  fetchPayment,
  getPaymentScheduleRequest,
  getPriceDetailFragment,
  proposalsQuery,
  proposalFields,
  proposalPriceQuery,
  proposalTransfersQuery,
  transportDetailsFragment,
  paymentProvidersRequest,
  getKnownAttendeesInformation,
  insurancesQuery,
  promoCodeProposalMutation,
  putServicesMutation,
  switchProposalsOffers,
};
